import { useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

export interface InlineEditFieldProps {
  value: string;
  onChange: (next: string) => void;
  onCommit: () => void;
  onCancel: () => void;
  label: string;
  placeholder?: string;
  multiline?: boolean;
  invalid?: boolean;
}

/**
 * The open state of an InlineEdit.
 *
 * Escape has to disarm the blur handler, otherwise leaving the field on cancel
 * would save the very edit the user just abandoned.
 */
export function InlineEditField({
  value,
  onChange,
  onCommit,
  onCancel,
  label,
  placeholder,
  multiline = false,
  invalid = false,
}: InlineEditFieldProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const cancelled = useRef(false);

  useEffect(() => {
    const field = wrapperRef.current?.querySelector<HTMLInputElement | HTMLTextAreaElement>(
      'input, textarea',
    );
    field?.focus();
    field?.select();
  }, []);

  const onKeyDown = (event: ReactKeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      cancelled.current = true;
      onCancel();
      return;
    }
    if (event.key === 'Enter' && (!multiline || event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      onCommit();
    }
  };

  const onBlur = () => {
    if (cancelled.current) return;
    onCommit();
  };

  const shared = {
    'aria-label': label,
    'aria-invalid': invalid || undefined,
    value,
    placeholder,
    onKeyDown,
    onBlur,
  };

  return (
    <div ref={wrapperRef}>
      {multiline ? (
        <Textarea
          {...shared}
          rows={3}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <Input {...shared} onChange={(event) => onChange(event.target.value)} />
      )}
    </div>
  );
}
